import { Check, Close, HelpOutline, Save } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Tab,
  Typography,
} from '@mui/material';
import { TabProps } from '@mui/material/Tab';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { DraggableDialog } from '~/components/Dialogs/components/DraggableDialog';
import { InstructionDialog } from '~/components/Instructions';
import {
  InstructionDialogConfig,
  useInstructionDialog,
} from '~/components/Instructions/useInstructionDialog';
import { SolaForceDialogTitle } from '~/components/SolaForceDialogTitle';
import { SolaForceTabs } from '~/components/SolaForceTabs';
import {
  EmpBasicData,
  EmpEmeContact,
  EmpHomeContact,
  EmpPosition,
} from '~/data/employee';
import { EmployeesIndexed } from '~/data/employees';
import {
  OverlappingDialogTabs,
  useOverlappingDialogTabs,
} from '~/features/OverlappingEmploymentDialog/useOverlappingDialogTabs';
import { useDialogState } from '~/hooks/useDialogState';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { LegacyMuiButton } from '~/legacy-components/LegacyForms';
import { isRoleHR, isRoleRecruiter } from '~/services/Permissions';

import { BasicDataTabForm } from './components/BasicDataTabForm';
import { ContactDataTabForm } from './components/ContactDataTabForm/component';
import { CustomDataTabForm } from './components/CustomDataTabForm/component';
import { EmploymentDetailTabForm } from './components/EmploymentDetailTabForm/component';
import { RewardingDataTabForm } from './components/RewardingDataTabForm/component';
import {
  saveAllowanceProposal,
  saveBasicData,
  saveBenefitProposal,
  saveCustomData,
  saveSalaryProposal,
  saveWorkingHours,
} from './data';
import {
  OverlappingEmpForm,
  useOverlappingEmploymentForm,
} from './hooks/useOverlappingEmploymentForm';
import { useSxStyles } from './useStyles';

interface Props {
  empId: number;
  empBasicData: EmpBasicData;
  empPosition: EmpPosition;
  empHomeContact: EmpHomeContact;
  empEmeContact: EmpEmeContact;
  allEmployees: EmployeesIndexed;
}

export const OverlappingEmploymentDialog = ({
  empId,
  empBasicData,
  empPosition,
  empHomeContact,
  empEmeContact,
  allEmployees,
}: Props) => {
  const sxStyles = useSxStyles();

  const roles = useLoggedInUserRoles();
  const iHR = isRoleHR(roles);
  const iRecruiter = isRoleRecruiter(roles);
  const loginId = useLoggedInEmployeeId();

  const overlappingDialogState = useDialogState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslation();
  const formValues = useOverlappingEmploymentForm({
    empPosition,
    allEmployees,
  });
  const {
    formProps: { handleSubmit, reset },
    helperValues: { initialFormValues, companyData, formTemplatesEnabled },
  } = formValues;

  const {
    tabConfigs,
    activeTab,
    resetTabs,
    onGoToNextTab,
    onGoToPreviousTab,
    onChangeTab,
    onComplete,
    isTabEnabled,
    isTabInvalid,
    getNextTab,
    getPreviousTab,
    confirmationDialogState,
  } = useOverlappingDialogTabs(formValues);

  const dialogOnClose = () => {
    overlappingDialogState.close();
    history.push(location.pathname);
    resetTabs();
    reset(initialFormValues);
  };

  const onAfterSubmit = (success: boolean) => {
    setIsSubmitting(false);
    if (success) {
      dialogOnClose();
      window.location.reload();
      enqueueSnackbar(t('rehireSuccess'), { variant: 'success' });
    } else {
      enqueueSnackbar(t('rehireError'), { variant: 'error' });
    }
  };

  const onSave: SubmitHandler<OverlappingEmpForm> = async (form) => {
    try {
      setIsSubmitting(true);
      const data = await saveBasicData(
        form,
        empBasicData,
        empEmeContact,
        empHomeContact,
        iHR || iRecruiter,
        onAfterSubmit,
      );
      const employeeId = data?.empData?.empBasicData?.fEmpId;
      const employmentId = data?.employment?.fEmplId;
      saveSalaryProposal(employeeId, employmentId, form, loginId, iHR);
      saveWorkingHours(employeeId, employmentId, form);
      saveBenefitProposal(employeeId, employmentId, form, loginId, iHR);
      saveAllowanceProposal(employeeId, employmentId, form, loginId, iHR);
      saveCustomData(employeeId, form);
    } catch (error) {
      setIsSubmitting(false);
      console.log('error', error);
    }
  };

  const isDialogOpen =
    overlappingDialogState.isOpen ||
    searchParams.get('mode') === 'overlapping-employment';

  const renderTabs = (tab: OverlappingDialogTabs) => {
    switch (tab) {
      case OverlappingDialogTabs.BasicData:
        return <BasicDataTabForm empId={empId} formValues={formValues} />;
      case OverlappingDialogTabs.Contact:
        return <ContactDataTabForm formValues={formValues} />;
      case OverlappingDialogTabs.CustomData:
        return <CustomDataTabForm empId={empId} formValues={formValues} />;
      case OverlappingDialogTabs.EmploymentDetails:
        return (
          <EmploymentDetailTabForm
            formValues={formValues}
            companyData={companyData}
          />
        );
      case OverlappingDialogTabs.Rewarding:
        return (
          <RewardingDataTabForm
            empId={empId}
            empPosition={empPosition}
            formValues={formValues}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <>
      <LegacyMuiButton
        onClick={
          formTemplatesEnabled
            ? () =>
                history.push(`/portal/employee/${empId}/overlapping-employment`)
            : overlappingDialogState.open
        }
        startIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Plus} />}
      >
        {t('laAdd')}
      </LegacyMuiButton>
      {isDialogOpen && (
        <DraggableDialog open={isDialogOpen} maxWidth={'lg'}>
          <SolaForceDialogTitle onClose={dialogOnClose}>
            {t('laAddNewEmployment')}
          </SolaForceDialogTitle>
          <Divider />
          <SolaForceTabs
            value={activeTab}
            onChange={(_event, value) => onChangeTab(value)}
          >
            {tabConfigs.map(({ props, instruction }) => {
              const disabled = !isTabEnabled(props.key);

              const tabSx = isTabInvalid(props.key) ? sxStyles.tabError : {};

              const iconProps: Partial<TabProps> = !!instruction
                ? {
                    icon: <InstructionComponent instruction={instruction} />,
                    iconPosition: 'end',
                    sx: { minHeight: '48px', ...tabSx },
                  }
                : { sx: tabSx };

              return <Tab {...props} {...iconProps} disabled={disabled} />;
            })}
          </SolaForceTabs>
          <DialogContent>
            <Box
              component={'form'}
              autoComplete="off"
              onSubmit={handleSubmit(onSave)}
              sx={{ width: '1000px' }}
            >
              {renderTabs(activeTab)}
            </Box>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            <Box>
              {!!getPreviousTab(activeTab) && (
                <Button onClick={onGoToPreviousTab}>{t('laBack')}</Button>
              )}
            </Box>
            <Box>
              {!!getNextTab(activeTab) ? (
                <Button onClick={onGoToNextTab}>{t('laContinue')}</Button>
              ) : (
                <Button
                  style={sxStyles.saveButton}
                  onClick={onComplete}
                  startIcon={<Save />}
                >
                  {t('laSave')}
                </Button>
              )}
            </Box>
          </DialogActions>
        </DraggableDialog>
      )}
      <Dialog open={confirmationDialogState.isOpen}>
        <SolaForceDialogTitle
          onClose={!isSubmitting ? confirmationDialogState.close : undefined}
        >
          {t('laAddNewEmployment')}
        </SolaForceDialogTitle>
        <Divider />
        <DialogContent>
          <Typography color={'error'} sx={sxStyles.confirmMessage}>
            {t('laConfirmAddNewEmployment')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={confirmationDialogState.close}
            disabled={isSubmitting}
            startIcon={<Close />}
          >
            {t('laNo')}
          </Button>
          <Button
            type="submit"
            onClick={() => {
              handleSubmit(onSave)();
            }}
            startIcon={
              isSubmitting ? <CircularProgress size={20} /> : <Check />
            }
            disabled={isSubmitting}
          >
            {t('laYes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const InstructionComponent = ({
  instruction,
}: {
  instruction?: InstructionDialogConfig;
}) => {
  const {
    dialogProps,
    dialogState: { toggle },
  } = useInstructionDialog(instruction);
  const sxStyles = useSxStyles();

  if (!instruction) return null;

  return (
    <>
      <HelpOutline
        fontSize={'small'}
        sx={sxStyles.helpIcon}
        onClick={(event: React.SyntheticEvent) => {
          event.stopPropagation();
          event.preventDefault();
          toggle();
        }}
      />
      {<InstructionDialog {...dialogProps} />}
    </>
  );
};
